/**
 * Configuration Scene: audio volume and difficulty settings.
 *
 * Displays:
 *   - Music Volume slider (9 stepped positions: 0% to 100% in 12.5% increments)
 *   - Sound Effects Volume slider (same 9 steps)
 *   - Master Sound Mute toggle
 *   - Difficulty selector (Easy / Normal / Hard)
 *
 * Settings persist to localStorage and take effect immediately.
 * Navigable with UP/DOWN arrows, LEFT/RIGHT to adjust, ESC/ENTER to return.
 */

import Phaser from 'phaser';
import {
  SCENE_CONFIG,
  SCENE_TITLE,
} from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  VOLUME_STEPS,
  DIFFICULTY_EASY,
  DIFFICULTY_NORMAL,
  DIFFICULTY_HARD,
  AUDIO_MENU_SELECT,
  AUDIO_MENU_CLICK,
  AUDIO_ESC_PRESSED,
  AUDIO_TITLE_BG,
} from '../config/game-config.js';
import { loadConfig, saveConfig } from '../utils/save-manager.js';
import { refreshConfig, getSfxVol } from '../utils/audio-config.js';

// --- Layout ---
const CX = GAME_WIDTH / 2;
const ROW_START_Y = 180;
const ROW_SPACING = 70;

// Slider track dimensions
const SLIDER_TRACK_W = 240;
const SLIDER_TRACK_H = 8;
const SLIDER_KNOB_W = 10;
const SLIDER_KNOB_H = 18;
const SLIDER_X = CX - SLIDER_TRACK_W / 2;  // left edge of track

// Difficulty bar dimensions
const DIFF_BAR_W = 240;
const DIFF_SEGMENT_W = DIFF_BAR_W / 3;

// --- Text Styles ---
const HEADER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '22px',
  color: '#ffffff',
  letterSpacing: 2,
};

const LABEL_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '14px',
  color: '#ffffff',
};

const VALUE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#aaaaaa',
};

const HINT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#888888',
};

const FOOTER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#666666',
};

/** Row identifiers for keyboard navigation. */
const Row = Object.freeze({
  MUSIC_VOL: 0,
  SFX_VOL: 1,
  MASTER_MUTE: 2,
  DIFFICULTY: 3,
});
const ROW_COUNT = 4;

export default class ConfigScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_CONFIG });
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(300, 0, 0, 0);

    /** @type {boolean} */
    this._transitioning = false;

    // Load current config
    /** @type {import('../utils/save-manager.js').ConfigSettings} */
    this._config = loadConfig();

    /** @type {number} Currently selected row. */
    this._selectedRow = Row.MUSIC_VOL;

    this._createHeader();
    this._createRows();
    this._createFooter();
    this._drawBorder();
    this._setupInput();

    // Defer initial visual update to the next frame so all Text canvases
    // are fully initialised (avoids Phaser drawImage-on-null errors).
    this.time.delayedCall(0, () => {
      this._updateAllVisuals();
    });
  }

  // ---------------------------------------------------------------
  // UI creation
  // ---------------------------------------------------------------

  /** @private */
  _createHeader() {
    this.add.text(CX, 60, 'CONFIGURATION', HEADER_STYLE).setOrigin(0.5);

    // Decorative line
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.3);
    g.lineBetween(CX - 120, 85, CX + 120, 85);
  }

  /** @private */
  _createRows() {
    // --- Row 0: Music Volume ---
    const musicY = ROW_START_Y;
    this._musicLabel = this.add.text(CX, musicY - 20, 'MUSIC VOLUME', LABEL_STYLE).setOrigin(0.5);
    this._musicTrackGfx = this.add.graphics();
    this._musicValueText = this.add.text(CX + SLIDER_TRACK_W / 2 + 30, musicY, '', VALUE_STYLE).setOrigin(0, 0.5);

    // --- Row 1: SFX Volume ---
    const sfxY = ROW_START_Y + ROW_SPACING;
    this._sfxLabel = this.add.text(CX, sfxY - 20, 'SOUND EFFECTS', LABEL_STYLE).setOrigin(0.5);
    this._sfxTrackGfx = this.add.graphics();
    this._sfxValueText = this.add.text(CX + SLIDER_TRACK_W / 2 + 30, sfxY, '', VALUE_STYLE).setOrigin(0, 0.5);

    // --- Row 2: Master Mute ---
    const muteY = ROW_START_Y + ROW_SPACING * 2;
    this._muteLabel = this.add.text(CX, muteY - 20, 'MASTER MUTE', LABEL_STYLE).setOrigin(0.5);
    this._muteGfx = this.add.graphics();
    this._muteValueText = this.add.text(CX + 30, muteY, '', VALUE_STYLE).setOrigin(0, 0.5);

    // --- Row 3: Difficulty ---
    const diffY = ROW_START_Y + ROW_SPACING * 3;
    this._diffLabel = this.add.text(CX, diffY - 20, 'DIFFICULTY', LABEL_STYLE).setOrigin(0.5);
    this._diffGfx = this.add.graphics();
    this._diffValueText = this.add.text(CX + DIFF_BAR_W / 2 + 30, diffY, '', VALUE_STYLE).setOrigin(0, 0.5);

    // Pre-create difficulty segment labels (must exist before _drawDifficultyBar)
    const diffBarX = CX - DIFF_BAR_W / 2;
    const diffLabels = ['EASY', 'NORMAL', 'HARD'];
    this._diffTexts = [];
    for (let j = 0; j < 3; j++) {
      const tx = diffBarX + j * DIFF_SEGMENT_W + DIFF_SEGMENT_W / 2;
      const t = this.add.text(tx, diffY, diffLabels[j], {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '10px',
        color: '#555555',
      }).setOrigin(0.5);
      this._diffTexts.push(t);
    }

    // Selector arrow
    this._selectorArrow = this.add.text(0, 0, '>', {
      fontFamily: '"Courier New", Courier, monospace',
      fontSize: '16px',
      color: '#ffffff',
    }).setOrigin(0.5);
  }

  /** @private */
  _createFooter() {
    this.add.text(CX, GAME_HEIGHT - 65, '\u2190 \u2192  Adjust   \u2191 \u2193  Navigate', HINT_STYLE).setOrigin(0.5);
    this.add.text(CX, GAME_HEIGHT - 45, 'ESC / ENTER  Return to title', FOOTER_STYLE).setOrigin(0.5);
  }

  /** @private */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }

  // ---------------------------------------------------------------
  // Input
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    this.input.keyboard.on('keydown-UP', () => this._navigateRow(-1));
    this.input.keyboard.on('keydown-DOWN', () => this._navigateRow(1));
    this.input.keyboard.on('keydown-LEFT', () => this._adjustValue(-1));
    this.input.keyboard.on('keydown-RIGHT', () => this._adjustValue(1));
    this.input.keyboard.on('keydown-ENTER', () => this._goBack());
    this.input.keyboard.on('keydown-ESC', () => this._goBack());

    this._setupMouseInput();
  }

  /**
   * Set up mouse interaction for all config rows:
   * - Labels: hover selects row
   * - Sliders: click on track sets volume step
   * - Mute: click toggles
   * - Difficulty: click on segment selects difficulty
   * @private
   */
  _setupMouseInput() {
    const rowLabels = [this._musicLabel, this._sfxLabel, this._muteLabel, this._diffLabel];

    // Make each row label interactive (hover to select row)
    rowLabels.forEach((label, rowIdx) => {
      label.setInteractive({ useHandCursor: true });
      label.on('pointerover', () => {
        if (this._transitioning) return;
        if (this._selectedRow !== rowIdx) {
          this._selectedRow = rowIdx;
          this._updateAllVisuals();
          try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) }); } catch (_) { /* */ }
        }
      });
    });

    // --- Slider hit zones (Music Volume & SFX Volume) ---
    const sliderRows = [
      { row: Row.MUSIC_VOL, y: ROW_START_Y },
      { row: Row.SFX_VOL, y: ROW_START_Y + ROW_SPACING },
    ];

    for (const sr of sliderRows) {
      const zone = this.add.zone(
        SLIDER_X + SLIDER_TRACK_W / 2,
        sr.y,
        SLIDER_TRACK_W + 10,
        SLIDER_KNOB_H + 8,
      ).setInteractive({ useHandCursor: true });

      zone.on('pointerdown', (pointer) => {
        if (this._transitioning) return;
        this._selectedRow = sr.row;
        this._setSliderFromPointer(sr.row, pointer.x);
      });

      // Drag support — keep adjusting while mouse is held
      zone.on('pointermove', (pointer) => {
        if (this._transitioning) return;
        if (pointer.isDown) {
          this._selectedRow = sr.row;
          this._setSliderFromPointer(sr.row, pointer.x);
        }
      });
    }

    // --- Mute toggle hit zone ---
    const muteY = ROW_START_Y + ROW_SPACING * 2;
    const muteZone = this.add.zone(CX, muteY, 60, 24)
      .setInteractive({ useHandCursor: true });

    muteZone.on('pointerdown', () => {
      if (this._transitioning) return;
      this._selectedRow = Row.MASTER_MUTE;
      this._adjustValue(1); // toggles mute
    });

    // --- Difficulty segment hit zones ---
    const diffY = ROW_START_Y + ROW_SPACING * 3;
    const diffBarX = CX - DIFF_BAR_W / 2;
    const levels = [DIFFICULTY_EASY, DIFFICULTY_NORMAL, DIFFICULTY_HARD];

    for (let j = 0; j < 3; j++) {
      const segCX = diffBarX + j * DIFF_SEGMENT_W + DIFF_SEGMENT_W / 2;
      const segZone = this.add.zone(segCX, diffY, DIFF_SEGMENT_W, 24)
        .setInteractive({ useHandCursor: true });

      segZone.on('pointerdown', () => {
        if (this._transitioning) return;
        this._selectedRow = Row.DIFFICULTY;
        if (this._config.difficulty !== levels[j]) {
          this._config.difficulty = levels[j];
          this._saveAndApply();
          this._updateAllVisuals();
          try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch (_) { /* */ }
        }
      });

      // Also make the text labels clickable
      this._diffTexts[j].setInteractive({ useHandCursor: true });
      this._diffTexts[j].on('pointerdown', () => {
        if (this._transitioning) return;
        this._selectedRow = Row.DIFFICULTY;
        if (this._config.difficulty !== levels[j]) {
          this._config.difficulty = levels[j];
          this._saveAndApply();
          this._updateAllVisuals();
          try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch (_) { /* */ }
        }
      });
    }
  }

  /**
   * Set a slider value based on pointer X position.
   * @private
   * @param {number} row — Row.MUSIC_VOL or Row.SFX_VOL
   * @param {number} pointerX — Pointer X in world coordinates
   */
  _setSliderFromPointer(row, pointerX) {
    // Map pointer X to a step index (0 to VOLUME_STEPS.length-1)
    const fraction = Phaser.Math.Clamp(
      (pointerX - SLIDER_X) / SLIDER_TRACK_W,
      0, 1,
    );
    const newStep = Math.round(fraction * (VOLUME_STEPS.length - 1));

    const key = row === Row.MUSIC_VOL ? 'musicVolumeStep' : 'sfxVolumeStep';
    if (this._config[key] === newStep) return;

    this._config[key] = newStep;
    this._saveAndApply();
    this._updateAllVisuals();

    try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch (_) { /* */ }

    this.sound.mute = this._config.masterMute;
    this._updateTitleMusicVolume();
  }

  /** @private */
  _navigateRow(delta) {
    if (this._transitioning) return;
    const next = ((this._selectedRow + delta) % ROW_COUNT + ROW_COUNT) % ROW_COUNT;
    this._selectedRow = next;
    this._updateAllVisuals();
    try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.5) }); } catch (_) { /* noop */ }
  }

  /** @private */
  _adjustValue(delta) {
    if (this._transitioning) return;

    switch (this._selectedRow) {
      case Row.MUSIC_VOL: {
        const newStep = Math.max(0, Math.min(VOLUME_STEPS.length - 1, this._config.musicVolumeStep + delta));
        if (newStep === this._config.musicVolumeStep) return;
        this._config.musicVolumeStep = newStep;
        break;
      }
      case Row.SFX_VOL: {
        const newStep = Math.max(0, Math.min(VOLUME_STEPS.length - 1, this._config.sfxVolumeStep + delta));
        if (newStep === this._config.sfxVolumeStep) return;
        this._config.sfxVolumeStep = newStep;
        break;
      }
      case Row.MASTER_MUTE: {
        // Toggle on any left/right press
        this._config.masterMute = !this._config.masterMute;
        break;
      }
      case Row.DIFFICULTY: {
        const levels = [DIFFICULTY_EASY, DIFFICULTY_NORMAL, DIFFICULTY_HARD];
        const idx = levels.indexOf(this._config.difficulty);
        const newIdx = Math.max(0, Math.min(2, idx + delta));
        if (newIdx === idx) return;
        this._config.difficulty = levels[newIdx];
        break;
      }
    }

    this._saveAndApply();
    this._updateAllVisuals();

    // Play feedback sound (using updated SFX volume)
    try { this.sound.play(AUDIO_MENU_SELECT, { volume: getSfxVol(0.4) }); } catch (_) { /* noop */ }

    // Apply master mute immediately to Phaser sound manager
    this.sound.mute = this._config.masterMute;

    // If music volume changed, update any playing title music
    this._updateTitleMusicVolume();
  }

  /** @private */
  _goBack() {
    if (this._transitioning) return;
    this._transitioning = true;

    try { this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.4) }); } catch (_) { /* noop */ }

    this.cameras.main.fadeOut(300, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }

  // ---------------------------------------------------------------
  // Save & apply
  // ---------------------------------------------------------------

  /** @private */
  _saveAndApply() {
    saveConfig(this._config);
    refreshConfig();  // Update the cached audio-config helper
  }

  /**
   * Update any currently playing title music volume in real-time.
   * Title music may still be running from TitleScene (kept alive through scene transitions).
   * @private
   */
  _updateTitleMusicVolume() {
    const existing = this.sound.get(AUDIO_TITLE_BG);
    if (existing && existing.isPlaying) {
      const multiplier = VOLUME_STEPS[this._config.musicVolumeStep] ?? 0.5;
      existing.volume = this._config.masterMute ? 0 : 0.4 * multiplier;
    }
  }

  // ---------------------------------------------------------------
  // Visual rendering
  // ---------------------------------------------------------------

  /** @private */
  _updateAllVisuals() {
    this._drawSlider(this._musicTrackGfx, ROW_START_Y, this._config.musicVolumeStep, this._selectedRow === Row.MUSIC_VOL);
    this._musicValueText.setText(this._volumeLabel(this._config.musicVolumeStep));
    this._musicLabel.setColor(this._selectedRow === Row.MUSIC_VOL ? '#ffffff' : '#aaaaaa');

    this._drawSlider(this._sfxTrackGfx, ROW_START_Y + ROW_SPACING, this._config.sfxVolumeStep, this._selectedRow === Row.SFX_VOL);
    this._sfxValueText.setText(this._volumeLabel(this._config.sfxVolumeStep));
    this._sfxLabel.setColor(this._selectedRow === Row.SFX_VOL ? '#ffffff' : '#aaaaaa');

    this._drawMuteToggle();
    this._muteLabel.setColor(this._selectedRow === Row.MASTER_MUTE ? '#ffffff' : '#aaaaaa');

    this._drawDifficultyBar();
    this._diffLabel.setColor(this._selectedRow === Row.DIFFICULTY ? '#ffffff' : '#aaaaaa');

    this._positionSelector();
  }

  /**
   * Draw a horizontal slider track with filled portion and knob.
   * @private
   * @param {Phaser.GameObjects.Graphics} gfx
   * @param {number} rowY - Center Y of the row
   * @param {number} step - Current step index (0 to VOLUME_STEPS.length-1)
   * @param {boolean} selected - Whether this row is focused
   */
  _drawSlider(gfx, rowY, step, selected) {
    gfx.clear();

    const maxStep = VOLUME_STEPS.length - 1;
    const trackY = rowY - SLIDER_TRACK_H / 2;
    const fillFraction = step / maxStep;

    // Track background (dark gray)
    gfx.fillStyle(0x333333, 1);
    gfx.fillRect(SLIDER_X, trackY, SLIDER_TRACK_W, SLIDER_TRACK_H);

    // Filled portion (white / bright)
    const fillColor = selected ? 0xffffff : 0x888888;
    gfx.fillStyle(fillColor, 1);
    gfx.fillRect(SLIDER_X, trackY, SLIDER_TRACK_W * fillFraction, SLIDER_TRACK_H);

    // Track border
    const borderColor = selected ? 0xffffff : 0x555555;
    gfx.lineStyle(1, borderColor, 0.8);
    gfx.strokeRect(SLIDER_X, trackY, SLIDER_TRACK_W, SLIDER_TRACK_H);

    // Step markers (internal lines at each main 25% position)
    for (let i = 1; i <= 3; i++) {
      const mx = SLIDER_X + (SLIDER_TRACK_W * i / 4);
      gfx.lineStyle(1, 0x555555, 0.5);
      gfx.lineBetween(mx, trackY + 1, mx, trackY + SLIDER_TRACK_H - 1);
    }

    // Minor tick marks at intermediate positions (12.5%, 37.5%, 62.5%, 87.5%)
    for (let i = 1; i <= 7; i += 2) {
      const mx = SLIDER_X + (SLIDER_TRACK_W * i / 8);
      gfx.lineStyle(1, 0x444444, 0.3);
      gfx.lineBetween(mx, trackY + 3, mx, trackY + SLIDER_TRACK_H - 3);
    }

    // Knob
    const knobX = SLIDER_X + (SLIDER_TRACK_W * fillFraction) - SLIDER_KNOB_W / 2;
    const knobY = rowY - SLIDER_KNOB_H / 2;
    const knobColor = selected ? 0xffffff : 0xaaaaaa;
    gfx.fillStyle(knobColor, 1);
    gfx.fillRect(knobX, knobY, SLIDER_KNOB_W, SLIDER_KNOB_H);

    // Knob border
    gfx.lineStyle(1, selected ? 0xffffff : 0x666666, 1);
    gfx.strokeRect(knobX, knobY, SLIDER_KNOB_W, SLIDER_KNOB_H);
  }

  /** @private */
  _drawMuteToggle() {
    this._muteGfx.clear();

    const rowY = ROW_START_Y + ROW_SPACING * 2;
    const selected = this._selectedRow === Row.MASTER_MUTE;
    const muted = this._config.masterMute;

    // Toggle box
    const boxSize = 16;
    const boxX = CX - boxSize / 2;
    const boxY = rowY - boxSize / 2;

    const borderColor = selected ? 0xffffff : 0x666666;
    this._muteGfx.lineStyle(1, borderColor, 1);
    this._muteGfx.strokeRect(boxX, boxY, boxSize, boxSize);

    if (muted) {
      // Filled X mark
      this._muteGfx.fillStyle(0xff4444, 1);
      this._muteGfx.fillRect(boxX + 2, boxY + 2, boxSize - 4, boxSize - 4);

      // X mark lines
      this._muteGfx.lineStyle(2, 0xffffff, 1);
      this._muteGfx.lineBetween(boxX + 3, boxY + 3, boxX + boxSize - 3, boxY + boxSize - 3);
      this._muteGfx.lineBetween(boxX + boxSize - 3, boxY + 3, boxX + 3, boxY + boxSize - 3);
    }

    this._muteValueText.setText(muted ? 'ALL MUTED' : 'OFF');
    this._muteValueText.setColor(muted ? '#ff4444' : '#888888');
  }

  /** @private */
  _drawDifficultyBar() {
    this._diffGfx.clear();

    const rowY = ROW_START_Y + ROW_SPACING * 3;
    const barX = CX - DIFF_BAR_W / 2;
    const barH = 20;
    const barY = rowY - barH / 2;
    const selected = this._selectedRow === Row.DIFFICULTY;

    const levels = [DIFFICULTY_EASY, DIFFICULTY_NORMAL, DIFFICULTY_HARD];
    const labels = ['EASY', 'NORMAL', 'HARD'];
    const activeIdx = levels.indexOf(this._config.difficulty);

    for (let i = 0; i < 3; i++) {
      const segX = barX + i * DIFF_SEGMENT_W;
      const isActive = i === activeIdx;

      // Background
      if (isActive) {
        const color = i === 0 ? 0x226622 : (i === 1 ? 0x555555 : 0x662222);
        this._diffGfx.fillStyle(color, 1);
        this._diffGfx.fillRect(segX, barY, DIFF_SEGMENT_W, barH);
      }

      // Border
      const borderColor = selected ? 0xaaaaaa : 0x444444;
      this._diffGfx.lineStyle(1, borderColor, 0.8);
      this._diffGfx.strokeRect(segX, barY, DIFF_SEGMENT_W, barH);
    }

    // Update difficulty segment text colors (texts created in _createRows)
    for (let i = 0; i < 3; i++) {
      const isActive = i === activeIdx;
      this._diffTexts[i].setColor(isActive ? '#ffffff' : '#555555');
    }

    // Value text on the right
    const diffName = labels[activeIdx] || 'NORMAL';
    this._diffValueText.setText(diffName);
    const diffColor = activeIdx === 0 ? '#44aa44' : (activeIdx === 2 ? '#aa4444' : '#aaaaaa');
    this._diffValueText.setColor(diffColor);
  }

  /**
   * Position the selector arrow next to the current row's label.
   * @private
   */
  _positionSelector() {
    const labels = [this._musicLabel, this._sfxLabel, this._muteLabel, this._diffLabel];
    const label = labels[this._selectedRow];
    if (!label) return;

    const targetX = label.x - label.width / 2 - 18;
    const targetY = label.y;

    this.tweens.killTweensOf(this._selectorArrow);
    this.tweens.add({
      targets: this._selectorArrow,
      x: targetX,
      y: targetY,
      duration: 100,
      ease: 'Quad.easeOut',
    });
  }

  /**
   * Convert a step index to a display string.
   * @private
   * @param {number} step
   * @returns {string}
   */
  _volumeLabel(step) {
    const pct = Math.round(VOLUME_STEPS[step] * 100);
    return `${pct}%`;
  }
}
